// A full-screen loading veil shown while scene assets (textures) are downloading — both at
// boot and when transferring between scenes (e.g. stepping through a portal into the city,
// whose assets are loaded lazily). Driven by THREE's DefaultLoadingManager: any TextureLoader
// without its own manager reports here, so the veil auto-shows while loads are in flight.
//
// During a scene transfer the controller "holds" the veil up (so it doesn't flicker off the
// instant textures finish, then reveal) and drops it explicitly once the new zone is shown.
//
// Look: solid-black pixel screen — "LOADING" in pixel-perfect Press Start 2P with cycling dots,
// and a chunky segmented block progress bar.

import * as THREE from "three";
import { injectTheme } from "./theme.js";

const STYLE_ID = "ghostland-loading-style";
const OVERLAY_ID = "gl-loading-overlay";

const CELLS = 18; // segmented progress blocks

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
#${OVERLAY_ID} {
  position: fixed; inset: 0; z-index: 40; display: none;
  flex-direction: column; align-items: center; justify-content: center; gap: 30px;
  background: #050208; image-rendering: pixelated;
}
#${OVERLAY_ID}.open { display: flex; }
#${OVERLAY_ID} .gl-load-text, #${OVERLAY_ID} .gl-load-text > span {
  /* !important to beat the theme's \`[id^="gl-"] * { font-family: … !important }\` rule */
  font-family: 'Press Start 2P', ui-monospace, monospace !important;
}
#${OVERLAY_ID} .gl-load-text {
  display: flex; align-items: baseline; font-size: 16px; line-height: 1;
  color: var(--gl-text, #f4f4f4); letter-spacing: 2px; text-shadow: 0 3px 0 rgba(0,0,0,.6);
}
#${OVERLAY_ID} .gl-load-dots { display: inline-block; width: 3ch; text-align: left; }
#${OVERLAY_ID} .gl-load-track { display: flex; gap: 3px; }
#${OVERLAY_ID} .gl-load-cell {
  width: 9px; height: 14px; background: #1b1922;
  box-shadow: inset 0 0 0 1px #000; image-rendering: pixelated;
}
#${OVERLAY_ID} .gl-load-cell.on { background: var(--gl-text, #f4f4f4); }
`;

let overlay: HTMLDivElement | null = null;
let labelEl: HTMLSpanElement | null = null;
let dotsEl: HTMLSpanElement | null = null;
let cells: HTMLDivElement[] = [];

let held = false;      // a scene transfer is keeping the veil up explicitly
let active = false;    // the loading manager currently has loads in flight
let transitionStart = 0; // performance.now() when the current transfer began
const MIN_TRANSITION_MS = 550; // keep the veil up at least this long so a transfer reads smoothly
const waiters: Array<() => void> = [];

// --- cycling "LOADING…" dots animation (runs only while the veil is visible) -
let raf = 0, animating = false, lastDot = 0, dots = 0;

function animLoop(t: number): void {
  if (!animating) return;
  raf = requestAnimationFrame(animLoop);
  if (t - lastDot > 350) { dots = (dots + 1) % 4; if (dotsEl) dotsEl.textContent = ".".repeat(dots); lastDot = t; }
}

function startAnim(): void {
  if (animating) return;
  animating = true;
  raf = requestAnimationFrame(animLoop);
}

function stopAnim(): void {
  animating = false;
  cancelAnimationFrame(raf);
}

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID; style.textContent = CSS; document.head.appendChild(style);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  const text = document.createElement("div");
  text.className = "gl-load-text";
  labelEl = document.createElement("span"); labelEl.className = "gl-load-label"; labelEl.textContent = "LOADING";
  dotsEl = document.createElement("span"); dotsEl.className = "gl-load-dots";
  text.append(labelEl, dotsEl);
  const track = document.createElement("div");
  track.className = "gl-load-track";
  cells = Array.from({ length: CELLS }, () => {
    const c = document.createElement("div"); c.className = "gl-load-cell"; track.appendChild(c); return c;
  });
  overlay.append(text, track);
  document.body.appendChild(overlay);
}

export function showLoading(label = "Loading"): void {
  ensure();
  if (labelEl) labelEl.textContent = label.toUpperCase();
  overlay!.classList.add("open");
  // Drive visibility inline too: a CSS-class-only opaque layer over the live WebGL canvas
  // can hit a compositor fast-path that leaves it unpainted; inline styles always paint.
  overlay!.style.display = "flex";
  overlay!.style.background = "#050208";
  startAnim();
}

export function hideLoading(): void {
  overlay?.classList.remove("open");
  if (overlay) overlay.style.display = "none";
  stopAnim();
}

function setProgress(p: number): void {
  const lit = Math.round(Math.max(0, Math.min(1, p)) * CELLS);
  cells.forEach((c, i) => c.classList.toggle("on", i < lit));
}

/** Hold the veil up across a scene transfer (won't auto-hide until endTransition). */
export function beginTransition(label = "Loading"): void {
  held = true;
  transitionStart = performance.now();
  showLoading(label);
  setProgress(0);
}

/** Release the transfer hold and drop the veil — kept up for a minimum time so even an
 *  instant (cached) load still reads as a smooth transition rather than a one-frame flash. */
export function endTransition(): void {
  const elapsed = performance.now() - transitionStart;
  const finish = (): void => { held = false; hideLoading(); };
  if (elapsed >= MIN_TRANSITION_MS) finish();
  else window.setTimeout(finish, MIN_TRANSITION_MS - elapsed);
}

/** Resolves once the loading manager has no loads in flight (immediately if already idle). */
export function whenAssetsIdle(): Promise<void> {
  if (!active) return Promise.resolve();
  return new Promise((resolve) => waiters.push(resolve));
}

// Wire the global manager once (module load). All plain TextureLoaders feed it.
const mgr = THREE.DefaultLoadingManager;
mgr.onStart = () => { active = true; showLoading(); };
mgr.onProgress = (_url, loaded, total) => { if (total) setProgress(loaded / total); };
mgr.onLoad = () => {
  active = false; setProgress(1);
  waiters.splice(0).forEach((f) => f());
  if (!held) hideLoading();
};
